import { useState } from 'react';
import type { Customer, UserSearch } from '@metron/shared';
import { USER_SEARCH_ROLES, USER_SEARCH_STATUSES, userSearchSchema } from '@metron/shared';
import { Button } from '../ui/button.tsx';
import { Input } from '../ui/input.tsx';
import { Label } from '../ui/label.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select.tsx';
import { CustomerPicker } from '../CustomerPicker.tsx';
import { type FilterChipItem, FilterChipList } from '../search/FilterChipList.tsx';

/** Spec fields a user can add or edit here; paging and sort belong to the table. */
type UserFilterField =
  | 'customerId'
  | 'text'
  | 'rbacRole'
  | 'enabled'
  | 'userStatus'
  | 'federated'
  | 'lastLoginOlderThanDays'
  | 'lastLoginWithinDays'
  | 'createdWithinDays';

export interface UserFilterChip extends FilterChipItem<UserFilterField> {
  fields: UserFilterField[];
}

// Distinct per role: the shared labels say "Admin" for three different roles.
export const USER_ROLE_LABELS: Record<(typeof USER_SEARCH_ROLES)[number], string> = {
  'utility-admin': 'Utility admin',
  'mhp-admin': 'MHP admin',
  'mhp-property-mgr': 'MHP property manager',
  'cmfi-admin': 'CMFI admin',
  'cmfi-property-mgr': 'CMFI property manager',
  'end-consumer': 'End consumer',
};

export const USER_STATUS_LABELS: Record<(typeof USER_SEARCH_STATUSES)[number], string> = {
  UNCONFIRMED: 'Unconfirmed',
  CONFIRMED: 'Confirmed',
  ARCHIVED: 'Archived',
  COMPROMISED: 'Compromised',
  UNKNOWN: 'Unknown',
  RESET_REQUIRED: 'Reset required',
  FORCE_CHANGE_PASSWORD: 'Never activated',
  EXTERNAL_PROVIDER: 'SSO',
};

const plural = (n: number) => `${n} day${n === 1 ? '' : 's'}`;

/** One chip per active filter, in a fixed order. The customer shows by name. */
export function describeUserFilters(spec: UserSearch, customerName?: string): UserFilterChip[] {
  const chips: UserFilterChip[] = [];
  const push = (id: UserFilterField, label: string) =>
    chips.push({ id, label, fields: [id], editable: true });
  if (spec.customerId !== undefined) {
    push('customerId', `Customer: ${customerName ?? 'selected customer'}`);
  }
  if (spec.text !== undefined) push('text', `Matches “${spec.text}”`);
  if (spec.rbacRole !== undefined) push('rbacRole', USER_ROLE_LABELS[spec.rbacRole]);
  if (spec.enabled !== undefined) push('enabled', spec.enabled ? 'Enabled' : 'Disabled');
  if (spec.userStatus !== undefined) {
    push('userStatus', `Status: ${USER_STATUS_LABELS[spec.userStatus]}`);
  }
  if (spec.federated !== undefined) push('federated', spec.federated ? 'SSO' : 'Password');
  if (spec.lastLoginOlderThanDays !== undefined) {
    push('lastLoginOlderThanDays', `No login in ${plural(spec.lastLoginOlderThanDays)}`);
  }
  if (spec.lastLoginWithinDays !== undefined) {
    push('lastLoginWithinDays', `Logged in within ${plural(spec.lastLoginWithinDays)}`);
  }
  if (spec.createdWithinDays !== undefined) {
    push('createdWithinDays', `Created within ${plural(spec.createdWithinDays)}`);
  }
  return chips;
}

/** Drop the given filter fields and return to page 1. */
export function withoutUserFields(spec: UserSearch, fields: UserFilterField[]): UserSearch {
  const next: UserSearch = { ...spec, page: 1 };
  for (const f of fields) delete next[f];
  return next;
}

type DayField = 'lastLoginOlderThanDays' | 'lastLoginWithinDays' | 'createdWithinDays';

const DAY_FIELDS: Array<{ field: DayField; label: string }> = [
  { field: 'lastLoginOlderThanDays', label: 'No login in (days)' },
  { field: 'lastLoginWithinDays', label: 'Logged in within (days)' },
  { field: 'createdWithinDays', label: 'Created within (days)' },
];

// Radix Select items can't have an empty value, so "no filter" is a sentinel.
const ANY = 'any';

interface Draft {
  customerId: number | undefined;
  text: string;
  rbacRole: string;
  enabled: string;
  userStatus: string;
  federated: string;
  days: Record<DayField, string>;
}

function toDraft(spec: UserSearch): Draft {
  const s = (v: number | undefined) => (v === undefined ? '' : String(v));
  const b = (v: boolean | undefined) => (v === undefined ? ANY : String(v));
  return {
    customerId: spec.customerId,
    text: spec.text ?? '',
    rbacRole: spec.rbacRole ?? ANY,
    enabled: b(spec.enabled),
    userStatus: spec.userStatus ?? ANY,
    federated: b(spec.federated),
    days: {
      lastLoginOlderThanDays: s(spec.lastLoginOlderThanDays),
      lastLoginWithinDays: s(spec.lastLoginWithinDays),
      createdWithinDays: s(spec.createdWithinDays),
    },
  };
}

interface EditorProps {
  spec: UserSearch;
  customers: Customer[];
  customersLoading: boolean;
  focusField: UserFilterField | null;
  onApply: (next: UserSearch) => void;
}

function UserFilterEditor({ spec, customers, customersLoading, focusField, onApply }: EditorProps) {
  const [draft, setDraft] = useState<Draft>(() => toDraft(spec));
  const [errors, setErrors] = useState<string[]>([]);

  const apply = (e: React.FormEvent) => {
    e.preventDefault();
    const bool = (v: string) => (v === ANY ? undefined : v === 'true');
    const candidate: Record<string, unknown> = {
      ...spec,
      page: 1,
      customerId: draft.customerId,
      text: draft.text.trim() === '' ? undefined : draft.text,
      rbacRole: draft.rbacRole === ANY ? undefined : draft.rbacRole,
      enabled: bool(draft.enabled),
      userStatus: draft.userStatus === ANY ? undefined : draft.userStatus,
      federated: bool(draft.federated),
    };
    for (const { field } of DAY_FIELDS) {
      const raw = draft.days[field].trim();
      candidate[field] = raw === '' ? undefined : Number(raw);
    }
    const parsed = userSearchSchema.safeParse(
      Object.fromEntries(Object.entries(candidate).filter(([, v]) => v !== undefined)),
    );
    if (!parsed.success) {
      setErrors(parsed.error.issues.map((i) => i.message));
      return;
    }
    setErrors([]);
    onApply(parsed.data);
  };

  const select = (
    id: 'rbacRole' | 'enabled' | 'userStatus' | 'federated',
    label: string,
    options: Array<[string, string]>,
  ) => (
    <div className='space-y-1'>
      <Label htmlFor={`user-search-${id}`}>{label}</Label>
      <Select
        name={id}
        value={draft[id]}
        onValueChange={(value) => setDraft({ ...draft, [id]: value })}
      >
        <SelectTrigger id={`user-search-${id}`} autoFocus={focusField === id}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ANY}>Any</SelectItem>
          {options.map(([value, text]) => <SelectItem key={value} value={value}>{text}
          </SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <form onSubmit={apply} className='space-y-3' aria-label='Edit filters'>
      <div className='space-y-1'>
        <Label htmlFor='user-search-customer'>Customer</Label>
        <CustomerPicker
          inputId='user-search-customer'
          customers={customers}
          isLoading={customersLoading}
          selectedCustomerId={draft.customerId}
          onSelect={(customerId) => setDraft({ ...draft, customerId })}
          onClear={() => setDraft({ ...draft, customerId: undefined })}
        />
      </div>
      <div className='space-y-1'>
        <Label htmlFor='user-search-text'>Email or name</Label>
        <Input
          id='user-search-text'
          value={draft.text}
          autoFocus={focusField === 'text'}
          onChange={(e) => setDraft({ ...draft, text: e.target.value })}
        />
      </div>
      <div className='grid grid-cols-2 gap-3'>
        {select('rbacRole', 'Role', Object.entries(USER_ROLE_LABELS))}
        {select('userStatus', 'Account status', Object.entries(USER_STATUS_LABELS))}
        {select('enabled', 'Enabled', [['true', 'Enabled'], ['false', 'Disabled']])}
        {select('federated', 'Sign-in', [['true', 'SSO'], ['false', 'Password']])}
      </div>
      <div className='grid grid-cols-3 gap-3'>
        {DAY_FIELDS.map(({ field, label }) => (
          <div key={field} className='space-y-1'>
            <Label htmlFor={`user-search-${field}`}>{label}</Label>
            <Input
              id={`user-search-${field}`}
              type='number'
              inputMode='numeric'
              min={1}
              step={1}
              value={draft.days[field]}
              autoFocus={focusField === field}
              onChange={(e) =>
                setDraft({ ...draft, days: { ...draft.days, [field]: e.target.value } })}
            />
          </div>
        ))}
      </div>
      {errors.length > 0 && (
        <ul role='alert' className='text-sm text-destructive list-disc pl-5'>
          {/* Replaced wholesale on each submit, and two issues can share a message. */}
          {errors.map((m, i) => <li key={i}>{m}</li>)}
        </ul>
      )}
      <div className='flex justify-end'>
        <Button type='submit' size='sm'>Apply</Button>
      </div>
    </form>
  );
}

interface UserSearchFiltersProps {
  spec: UserSearch;
  customers: Customer[];
  customersLoading: boolean;
  onChange: (next: UserSearch) => void;
}

/** Active user filters as chips plus an Add filter editor. */
export function UserSearchFilters(
  { spec, customers, customersLoading, onChange }: UserSearchFiltersProps,
) {
  const customerName = customers.find((c) => c.id === spec.customerId)?.waterscopeAccountName;
  return (
    <FilterChipList
      chips={describeUserFilters(spec, customerName)}
      emptyText='No filters: showing all WaterScope Plus users.'
      onRemove={(chip) => onChange(withoutUserFields(spec, chip.fields))}
      renderEditor={(focusField, close) => (
        <UserFilterEditor
          spec={spec}
          customers={customers}
          customersLoading={customersLoading}
          focusField={focusField}
          onApply={(next) => {
            close();
            onChange(next);
          }}
        />
      )}
    />
  );
}
